import { useMemo } from 'react'
import { GroupOption } from '@/types/metrics'
import { formatter, getTimeTicks } from '@/utils/chart'
import dayjs from 'dayjs'

type UseChartTicksOptions = {
  from: Date
  to: Date
  groupedBy: GroupOption
  dataLength: number
  useRanges?: boolean
}

export default function useTimeChartTicks({
  from,
  to,
  dataLength,
  groupedBy,
  useRanges = true,
}: UseChartTicksOptions) {
  const includeYear = useMemo(
    () => from.getFullYear() !== to.getFullYear(),
    [from, to],
  )
  const timeScaleTicks = useMemo(
    () => getTimeTicks(from, to, groupedBy, includeYear),
    [from, groupedBy, includeYear, to],
  )

  const { needsToRotate, showingRange = false } = useMemo(() => {
    if (['hour', 'day'].includes(groupedBy)) {
      return { needsToRotate: dataLength > 30 }
    }

    if (groupedBy === 'week') {
      return { needsToRotate: dataLength > 10, showingRange: true }
    }
    let willDisplayRange = false
    if (useRanges) {
      if (
        from.getDate() !== 1 ||
        !dayjs(to).endOf('month').isSame(dayjs(to), 'day')
      ) {
        willDisplayRange = true
      }
    }
    if (groupedBy === 'month') {
      if (willDisplayRange) {
        return {
          needsToRotate: includeYear ? dataLength > 10 : dataLength > 20,
          showingRange: true,
        }
      }
      return { needsToRotate: includeYear ? dataLength > 15 : dataLength > 25 }
    }

    return {
      needsToRotate: willDisplayRange ? dataLength > 70 : dataLength > 80,
      showingRange: willDisplayRange,
    }
  }, [dataLength, from, groupedBy, includeYear, to, useRanges])

  const axisBottom = {
    format: (value: string) => {
      const formattedDate = formatter(
        value,
        groupedBy,
        includeYear,
        to,
        useRanges,
      )
      return dataLength <= 100 ||
        (dataLength > 100 && timeScaleTicks.includes(formattedDate))
        ? formattedDate
        : ''
    },
    tickSize: 5,
    tickPadding: 5,
    tickRotation: needsToRotate ? 90 : 0,
  }

  return {
    axisBottom,
    includeYear,
    needsToRotate,
    showingRange,
    timeScaleTicks,
  }
}
